import {
  Box,
  Button,
  Divider,
  Flex,
  FormLabel,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import * as _ from 'lodash';
import React from 'react';

import {
  CreditAgreement,
  Deliverable,
} from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import {
  CheckboxFormField,
  FormFieldLabel,
  InputFormField,
} from 'packages/common-frontend/components/FormField';
import {
  Select,
  formatSelectOptions,
} from 'packages/common-frontend/components/Select';
import { formatBasePropertyKey } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { ReportingPeriod } from '~/packages/creditAgreements/ui/ReportingPeriod';

const createDeliverableArtifactConfigShell = (
  isFinalArtifact: boolean = false,
): CreditAgreement.Config.DeliverableArtifactProperty.Config => ({
  type: 'cashBalancesFeed',
  configKey: isFinalArtifact
    ? 'setFinalArtifactConfigKey'
    : 'setDependencyConfigKey',
  relativeRecurringDateConfig: {
    relativeRecurringDateConfig: [],
    exclusions: [],
  },
});

export const createDeliverableArtifactPropertyShell = (
  isFinalArtifact: boolean = false,
): CreditAgreement.DomainEntity.DeliverableArtifactProperty => ({
  name: 'Deliverable Artifact',
  description: 'description of deliverable artifact',
  type: CreditAgreement.DomainEntity.PropertyType.DeliverableProperty,
  value: 'description of deliverable artifact',
  reportingPeriod: {
    period: isFinalArtifact ? 'calendarMonth' : 'calendarWeek',
  },
  _config_: createDeliverableArtifactConfigShell(isFinalArtifact),
  _isWrittenIn_: true,
});

const createDeliverableConfigShell = (): CreditAgreement.Config.DeliverableProperty.Config => ({
  configKey: 'setConfigKey',
  recurringDueDateConfig: { recurringDateConfigs: [], exclusions: [] },
  daysOffset: Deliverable.DAYS_BEFORE_DUE_DATE,
});

const createDeliverablePropertyShell = (): CreditAgreement.DomainEntity.DeliverableProperty => ({
  name: 'Deliverable',
  description: 'description of deliverable',
  type: CreditAgreement.DomainEntity.PropertyType.DeliverableProperty,
  value: 'description of deliverable',
  containerType: 'settlementReport',
  reportingPeriod: { period: 'calendarMonth' },
  finalArtifacts: [createDeliverableArtifactPropertyShell(true)],
  dependencies: [createDeliverableArtifactPropertyShell()],
  _config_: createDeliverableConfigShell(),
  _isWrittenIn_: true,
});

type EditDeliverableArtifactModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  basePropertyKey: string;
  wipConfig: WipConfig;
  deliverableArtifact: CreditAgreement.DomainEntity.DeliverableArtifactProperty;
  onSubmit: (props: any) => void;
};
export const EditDeliverableArtifactModal = ({
  isOpen,
  setIsOpen,
  basePropertyKey,
  wipConfig: { formProps },
  deliverableArtifact,
  onSubmit,
}: EditDeliverableArtifactModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Property</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDirection="row" alignItems="flex-start" mb={5}>
            <FormLabel w="60" mb="0">
              Property
            </FormLabel>
            <Box width="100%">
              <Text width="sm">{formatBasePropertyKey(basePropertyKey)}</Text>
            </Box>
          </Flex>
          <Formik
            onSubmit={(props) => {
              onSubmit(props);
              setIsOpen(false);
            }}
            initialValues={{
              ...deliverableArtifact,
            }}
          >
            {(localFormProps) => {
              return (
                <Form onSubmit={localFormProps.handleSubmit}>
                  <VStack spacing={5}>
                    <Flex
                      width="100%"
                      flexDirection="row"
                      alignItems="flex-start"
                    >
                      <FormFieldLabel label="Name" labelWidth="60" />
                      <InputGroup w="100%">
                        <Input
                          type="text"
                          data-testid="name"
                          name="name"
                          value={localFormProps.values.name}
                          onChange={(e) => {
                            const value = e.target.value;
                            localFormProps.setFieldValue('name', value);
                            localFormProps.setFieldValue(
                              '_config_.configKey',
                              _.camelCase(value),
                            );
                            localFormProps.setFieldValue('value', value);
                          }}
                          onBlur={formProps.handleBlur}
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    <InputFormField
                      propName="description"
                      label="Description"
                      formProps={localFormProps}
                      value={localFormProps.values.description}
                      labelWidth="60"
                      inputWidth="sm"
                      flexDirection="row"
                      alignItems="flex-end"
                    />
                    <InputFormField
                      propName="section"
                      label="Section"
                      formProps={localFormProps}
                      value={localFormProps.values.section}
                      labelWidth="60"
                      inputWidth="sm"
                      flexDirection="row"
                      alignItems="flex-end"
                    />
                    <Divider pb={4} />
                    <Flex
                      width="100%"
                      flexDirection="column"
                      alignItems="flex-start"
                      pb={4}
                    >
                      <ReportingPeriod
                        reportingPeriod={localFormProps.values.reportingPeriod}
                        setReportingPeriod={(period) => {
                          localFormProps.setFieldValue(
                            'reportingPeriod',
                            period,
                          );
                        }}
                      />
                    </Flex>
                    <Divider />
                    <CheckboxFormField
                      propName="_isWrittenIn_"
                      label="Is Written In"
                      formProps={localFormProps}
                      value={localFormProps.values._isWrittenIn_}
                      labelWidth="60"
                      inputWidth="sm"
                      flexDirection="row"
                    />
                  </VStack>
                  <Flex justifyContent="center" mt="8">
                    <Button
                      type="submit"
                      aria-label="Done"
                      isDisabled={_.keys(formProps.errors).length > 0}
                    >
                      Done
                    </Button>
                  </Flex>
                </Form>
              );
            }}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type EditDeliverableModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  basePropertyKey: string;
  wipConfig: WipConfig;
  deliverable?: CreditAgreement.DomainEntity.DeliverableProperty;
  onSubmit: (props: any) => void;
};
export const EditDeliverableModal = ({
  isOpen,
  setIsOpen,
  basePropertyKey,
  wipConfig: { formProps },
  deliverable,
  onSubmit,
}: EditDeliverableModalProps) => {
  const initialValues: CreditAgreement.DomainEntity.DeliverableProperty =
    deliverable || createDeliverablePropertyShell();

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Property</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDirection="row" alignItems="flex-start" mb={5}>
            <FormLabel w="60" mb="0">
              Property
            </FormLabel>
            <Box width="100%">
              <Text width="sm">{formatBasePropertyKey(basePropertyKey)}</Text>
            </Box>
          </Flex>
          <Formik
            onSubmit={(props) => {
              onSubmit(props);
              setIsOpen(false);
            }}
            initialValues={{
              ...initialValues,
            }}
          >
            {(localFormProps) => {
              return (
                <Form onSubmit={localFormProps.handleSubmit}>
                  <VStack spacing={5}>
                    <Flex
                      width="100%"
                      flexDirection="row"
                      alignItems="flex-start"
                    >
                      <FormFieldLabel label="Name" labelWidth="60" />
                      <InputGroup w="100%">
                        <Input
                          type="text"
                          data-testid="name"
                          name="name"
                          value={localFormProps.values.name}
                          onChange={(e) => {
                            const value = e.target.value;
                            localFormProps.setFieldValue('name', value);
                            localFormProps.setFieldValue(
                              '_config_.configKey',
                              _.camelCase(value),
                            );
                            localFormProps.setFieldValue('value', value);
                          }}
                          onBlur={formProps.handleBlur}
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    <InputFormField
                      propName="description"
                      label="Description"
                      formProps={localFormProps}
                      value={localFormProps.values.description}
                      labelWidth="60"
                      inputWidth="sm"
                      flexDirection="row"
                      alignItems="flex-end"
                    />
                    <InputFormField
                      propName="section"
                      label="Section"
                      formProps={localFormProps}
                      value={localFormProps.values.section}
                      labelWidth="60"
                      inputWidth="sm"
                      flexDirection="row"
                      alignItems="flex-end"
                    />
                    <Flex
                      width="100%"
                      flexDirection="row"
                      alignItems="flex-start"
                    >
                      <FormFieldLabel label="Container Type" labelWidth="40" />
                      <Select
                        value={localFormProps.values.containerType}
                        options={formatSelectOptions(
                          Deliverable.DeliverableContainerTypes,
                          'sentence',
                        )}
                        onChange={(value) => {
                          if (value) {
                            localFormProps.setFieldValue(
                              'containerType',
                              value,
                            );
                          }
                        }}
                      />
                    </Flex>
                    <Divider pb={4} />
                    <Flex
                      width="100%"
                      flexDirection="column"
                      alignItems="flex-start"
                      pb={4}
                    >
                      <ReportingPeriod
                        reportingPeriod={localFormProps.values.reportingPeriod}
                        setReportingPeriod={(period) => {
                          localFormProps.setFieldValue(
                            'reportingPeriod',
                            period,
                          );
                        }}
                      />
                    </Flex>
                    <Divider />
                    <CheckboxFormField
                      propName="_isWrittenIn_"
                      label="Is Written In"
                      formProps={localFormProps}
                      value={localFormProps.values._isWrittenIn_}
                      labelWidth="60"
                      inputWidth="sm"
                      flexDirection="row"
                    />
                  </VStack>
                  <Flex justifyContent="center" mt="8">
                    <Button
                      type="submit"
                      aria-label="Done"
                      isDisabled={_.keys(formProps.errors).length > 0}
                    >
                      Done
                    </Button>
                  </Flex>
                </Form>
              );
            }}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
